import {useCallback, useState, Dispatch, SetStateAction} from 'react';

import {EntitySummary} from 'constants/UserConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {getSchemaOwnerChoices} from 'schemas/spec/SchemaSpecUtil';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';

import type {EntryTemplateForConfigureButton} from './ConfigureExecutionTemplateButton';
import {DEFAULT_TASK_SCHEMA_PREFIX} from './ProcessTaskSchemaEditorConstants';
import type {MinimalProcessTaskSchema, UpdateProcessSchema} from './ProcessTaskSchemaUtils';
import {useFolderState} from './useFolderState';
import {useResponsiblePartiesState} from './useResponsiblePartiesState';
import {useStatusLifecycleState} from './useStatusLifecycleState';

type Setter<T> = Dispatch<SetStateAction<T>>;

export interface SchemaFormStateArgs {
  processTaskSchema: MinimalProcessTaskSchema | null;
  statusLifecycles: Array<{id: string; name: string; schemaExecutionType: ProcessExecutionType}> | null;
  restrictedToExecutionType?: ProcessExecutionType;
  restrictedToOrganizationId?: string;
  locked?: boolean;
}

export interface SchemaFormState {
  taskGroupPrefix: string;
  processTaskSchemaName: string;
  processTaskSchemaSqlIdentifier: string | undefined;
  executionType: ProcessExecutionType;
  taskSchemaPrefix: string;
  schemaOwner: EntitySummary | null;
  schemaOwnerChoices: Array<EntitySummary>;
  canSetAssigneeOnTaskCreation: boolean;
  isPropagateWatchersEnabled: boolean | undefined;
  canOnlyBePartOfFlowchart: boolean;
  canConfigureFlowchart: boolean;
  isEditingFlowchart: boolean;
  errors: {[errorKey: string]: string};
  defaultResponsibleParties: Array<MinimalEntity>;
  areDefaultResponsiblePartiesValid: boolean;
  showDefaultResponsiblePartiesError: boolean;
  responsiblePartyLookupConfig: LookupConfigSpec | null;
  taskFinderLookupConfig: LookupConfigSpec | null;
  executionTemplate: EntryTemplateForConfigureButton;
  defaultCreationFolderId: string | null;
  isDefaultCreationFolderInaccessible: boolean;
  defaultEntryExecutionFolderId: string | null;
  isDefaultEntryExecutionFolderInaccessible: boolean;
  taskGroupInstructions: string | null | undefined;
  statusLifecycle: {id: string; name: string} | undefined;
  statusLifecyclesForExecutionType: Array<{
    id: string;
    name: string;
    schemaExecutionType: ProcessExecutionType;
  }>;

  setTaskGroupPrefix: Setter<string>;
  setProcessTaskSchemaName: Setter<string>;
  setProcessTaskSchemaSqlIdentifier: Setter<string | undefined>;
  setExecutionType: Setter<ProcessExecutionType>;
  setTaskSchemaPrefix: Setter<string>;
  setSchemaOwner: Setter<EntitySummary | null>;
  setCanSetAssigneeOnTaskCreation: Setter<boolean>;
  setIsPropagateWatchersEnabled: Setter<boolean | undefined>;
  setCanOnlyBePartOfFlowchart: Setter<boolean>;
  setCanConfigureFlowchart: Setter<boolean>;
  setIsEditingFlowchart: Setter<boolean>;
  setErrors: Setter<{[errorKey: string]: string}>;
  setAreDefaultResponsiblePartiesValid: Setter<boolean>;
  setShowDefaultResponsiblePartiesError: Setter<boolean>;
  setExecutionTemplate: Setter<EntryTemplateForConfigureButton>;
  setStatusLifecycle: Setter<{id: string; name: string} | undefined>;

  applyFormUpdates: (params: UpdateProcessSchema & {updateTables?: boolean}) => void;
}

export function useSchemaFormState(args: SchemaFormStateArgs): SchemaFormState {
  const {processTaskSchema, statusLifecycles, restrictedToExecutionType, restrictedToOrganizationId} = args;

  // --- Schema identity / metadata ---
  const [taskGroupPrefix, setTaskGroupPrefix] = useState(processTaskSchema?.taskGroupPrefix ?? '');
  const [processTaskSchemaName, setProcessTaskSchemaName] = useState(processTaskSchema?.name ?? '');
  const [processTaskSchemaSqlIdentifier, setProcessTaskSchemaSqlIdentifier] = useState(
    processTaskSchema?.sqlIdentifier
  );
  const [executionType, setExecutionType] = useState(
    processTaskSchema?.executionType ?? restrictedToExecutionType ?? ProcessExecutionType.ENTRY
  );
  const [taskSchemaPrefix, setTaskSchemaPrefix] = useState(
    processTaskSchema?.prefix ?? DEFAULT_TASK_SCHEMA_PREFIX
  );
  const schemaOwnerChoices = getSchemaOwnerChoices();
  const initialSchemaOwner =
    restrictedToOrganizationId != null
      ? schemaOwnerChoices.find(({id}) => id === restrictedToOrganizationId)
      : schemaOwnerChoices[0];
  const [schemaOwner, setSchemaOwner] = useState<EntitySummary | null>(
    processTaskSchema?.organization ?? initialSchemaOwner ?? null
  );

  // --- Behavior flags ---
  const [canSetAssigneeOnTaskCreation, setCanSetAssigneeOnTaskCreation] = useState(
    processTaskSchema?.canSetAssigneeOnTaskCreation ?? true
  );
  const [isPropagateWatchersEnabled, setIsPropagateWatchersEnabled] = useState(
    processTaskSchema?.isPropagateWatchersEnabled
  );
  const [canOnlyBePartOfFlowchart, setCanOnlyBePartOfFlowchart] = useState(
    processTaskSchema?.canOnlyBePartOfFlowchart ?? false
  );
  const [canConfigureFlowchart, setCanConfigureFlowchart] = useState(
    processTaskSchema?.executionType === ProcessExecutionType.FLOWCHART
  );
  const [isEditingFlowchart, setIsEditingFlowchart] = useState(false);
  const [errors, setErrors] = useState<{[errorKey: string]: string}>({});

  // --- Execution template / task finder / instructions ---
  const [taskFinderLookupConfig, setTaskFinderLookupConfig] = useState<LookupConfigSpec | null>(
    processTaskSchema?.outputSchema?.taskFinderLookupConfig ?? null
  );
  const [executionTemplate, setExecutionTemplate] = useState<EntryTemplateForConfigureButton>(
    processTaskSchema?.entryTemplate ?? null
  );
  const [taskGroupInstructions, setTaskGroupInstructions] = useState(
    processTaskSchema?.taskGroupInstructions
  );

  // --- Composed sub-hooks ---
  const folderState = useFolderState(processTaskSchema);
  const responsiblePartiesState = useResponsiblePartiesState(processTaskSchema);
  const lifecycleState = useStatusLifecycleState({
    processTaskSchema,
    statusLifecycles,
    executionType,
    setExecutionTemplate,
    setDefaultEntryExecutionFolderId: folderState.setDefaultEntryExecutionFolderId,
  });

  // --- Composed applyFormUpdates ---
  const applyFormUpdates = useCallback(
    (params: UpdateProcessSchema & {updateTables?: boolean}): void => {
      if (params.taskGroupPrefix != null) {
        setTaskGroupPrefix(params.taskGroupPrefix);
      }
      if (params.name != null) {
        setProcessTaskSchemaName(params.name);
      }
      if (params.sqlIdentifier != null) {
        setProcessTaskSchemaSqlIdentifier(params.sqlIdentifier);
      }
      if (params.taskSchemaPrefix != null) {
        setTaskSchemaPrefix(params.taskSchemaPrefix);
      }
      if (params.updateTaskFinder) {
        setTaskFinderLookupConfig(params.taskFinderLookupConfig ?? null);
      }
      if (params.canSetAssigneeOnTaskCreation != null) {
        setCanSetAssigneeOnTaskCreation(params.canSetAssigneeOnTaskCreation);
      }
      if (params.canOnlyBePartOfFlowchart != null) {
        setCanOnlyBePartOfFlowchart(params.canOnlyBePartOfFlowchart);
      }
      if (params.taskGroupInstructions !== undefined) {
        setTaskGroupInstructions(params.taskGroupInstructions);
      }
      if (params.isPropagateWatchersEnabled != null) {
        setIsPropagateWatchersEnabled(params.isPropagateWatchersEnabled);
      }
      folderState.applyFolderUpdates(params);
      responsiblePartiesState.applyResponsiblePartiesUpdates(params);
    },
    [folderState, responsiblePartiesState]
  );

  return {
    taskGroupPrefix,
    processTaskSchemaName,
    processTaskSchemaSqlIdentifier,
    executionType,
    taskSchemaPrefix,
    schemaOwner,
    schemaOwnerChoices,
    canSetAssigneeOnTaskCreation,
    isPropagateWatchersEnabled,
    canOnlyBePartOfFlowchart,
    canConfigureFlowchart,
    isEditingFlowchart,
    errors,
    defaultResponsibleParties: responsiblePartiesState.defaultResponsibleParties,
    areDefaultResponsiblePartiesValid: responsiblePartiesState.areDefaultResponsiblePartiesValid,
    showDefaultResponsiblePartiesError: responsiblePartiesState.showDefaultResponsiblePartiesError,
    responsiblePartyLookupConfig: responsiblePartiesState.responsiblePartyLookupConfig,
    taskFinderLookupConfig,
    executionTemplate,
    defaultCreationFolderId: folderState.defaultCreationFolderId,
    isDefaultCreationFolderInaccessible: folderState.isDefaultCreationFolderInaccessible,
    defaultEntryExecutionFolderId: folderState.defaultEntryExecutionFolderId,
    isDefaultEntryExecutionFolderInaccessible: folderState.isDefaultEntryExecutionFolderInaccessible,
    taskGroupInstructions,
    statusLifecycle: lifecycleState.statusLifecycle,
    statusLifecyclesForExecutionType: lifecycleState.statusLifecyclesForExecutionType,

    setTaskGroupPrefix,
    setProcessTaskSchemaName,
    setProcessTaskSchemaSqlIdentifier,
    setExecutionType,
    setTaskSchemaPrefix,
    setSchemaOwner,
    setCanSetAssigneeOnTaskCreation,
    setIsPropagateWatchersEnabled,
    setCanOnlyBePartOfFlowchart,
    setCanConfigureFlowchart,
    setIsEditingFlowchart,
    setErrors,
    setAreDefaultResponsiblePartiesValid: responsiblePartiesState.setAreDefaultResponsiblePartiesValid,
    setShowDefaultResponsiblePartiesError: responsiblePartiesState.setShowDefaultResponsiblePartiesError,
    setExecutionTemplate,
    setStatusLifecycle: lifecycleState.setStatusLifecycle,

    applyFormUpdates,
  };
}
